import { useModal, create, show } from "@ebay/nice-modal-react";
import { FormProvider, useForm } from "react-hook-form";

import useTypedContext from "hooks/useTypedContext";
import FlashContext from "components/FlashMessages/FlashContext";
import DeleteConfirmationModal from "ds/components/DeleteConfirmationModal";
import { ConfirmationModalMessage } from "ds/components/ConfirmationModal/Message";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPageOrganization } from "hooks/useAnalytics/pages/organization";
import { createDefaultMutationHook } from "hooks/useMutationHandler";
import FormFieldTextConfirm from "components/FormFields/Confirm";
import Box from "ds/components/Box";

import { DELETE_OIDC_SSO } from "./gql";

const useDeleteOIDC = createDefaultMutationHook(DELETE_OIDC_SSO);

type SettingsSingleSignOnDisableOIDCProps = {
  hasManagedUsers?: boolean;
};

const SettingsSingleSignOnDisableOIDC = create(function SettingsSingleSignOnDisableOIDC({
  hasManagedUsers,
}: SettingsSingleSignOnDisableOIDCProps) {
  const modal = useModal();

  const trackSegmentAnalyticsEvent = useAnalytics({
    page: AnalyticsPageOrganization.OrganizationSSO,
    defaultCallbackTrackProperties: { method: "OIDC" },
  });

  const { reportSuccess } = useTypedContext(FlashContext);

  const [deleteOIDC, { loading, data }] = useDeleteOIDC({
    refetchQueries: ["GetSSOSettings"],
  });

  const handleDelete = () => {
    trackSegmentAnalyticsEvent("Confirm disable click");
    deleteOIDC({}, (data) => {
      if (data?.oidcDelete) {
        reportSuccess({ message: "OIDC successfully disabled" });
        modal.hide();
      }
    });
  };

  const OIDCUnlinkForm = useForm<{ deleteConfirmationText: string }>({
    defaultValues: { deleteConfirmationText: "" },
    mode: "onChange",
  });

  return (
    <DeleteConfirmationModal
      title="Unlink OIDC SSO"
      onConfirm={handleDelete}
      isLoading={loading}
      isDismissable={!loading && !data?.oidcDelete?.clientId}
      confirmationButtonLabel="Unlink"
      isConfirmationDisabled={loading || !OIDCUnlinkForm.formState.isValid}
    >
      <Box fullWidth padding="0 0 large 0">
        <ConfirmationModalMessage>
          Are you sure you want to unlink current OIDC integration?{" "}
          {hasManagedUsers && (
            <>
              <strong>OIDC managed users will be removed and this action cannot be undone.</strong>
            </>
          )}
        </ConfirmationModalMessage>
      </Box>

      <FormProvider {...OIDCUnlinkForm}>
        <FormFieldTextConfirm
          fieldName="deleteConfirmationText"
          textToConfirm="unlink"
          disabled={loading}
        />
      </FormProvider>
    </DeleteConfirmationModal>
  );
});

export const showDisableConfirmation = (props: SettingsSingleSignOnDisableOIDCProps) =>
  show(SettingsSingleSignOnDisableOIDC, props);
